import { Temporal } from '@js-temporal/polyfill';
import { useCallback, useMemo, useState } from 'react';

import { today } from '@/lib/dates';
import { buildLOS } from '@/lib/los';
import { calculatePricing } from '@/lib/pricing/stay-price';
import type { IStay } from '@/lib/stay';
import type { IContent, IDirectDiscount, IRestriction } from '@/types/content';
import type { ILOS } from '@/types/pricing';

export interface StayDates {
	startOn: string;
	endOn: string;
}

export interface StayGuests {
	adults: number;
	children: number;
	/** Only where the property charges a pet fee. */
	pets?: number;
}

interface UseStayPricingOptions {
	content: IContent;
	/** The guest booking fee as a rate, e.g. 0.1 for 10%. */
	bookingFeeRate: number;
	initialDates?: StayDates;
	initialGuests?: StayGuests;
	/** `undefined` = no code given, `null` = a code that did not match. */
	initialDiscount?: IDirectDiscount | null;
	/** The property's `pet_fees` flag is on: guests choose their pets and pay its pet fee. */
	petFees?: boolean;
}

export interface StayPricing {
	/** The priced stay, ready for the booking card, summary and payment. */
	stay: IStay;
	/** Length-of-stay price table the calendar uses to decide which dates can be picked. */
	los: ILOS;
	/** The first date a guest may check in, given the property's notice period. */
	firstSelectableDate: string;
	dates: StayDates;
	guests: StayGuests;
	discount: IDirectDiscount | null | undefined;
	setDates: (dates: StayDates) => void;
	setGuests: (guests: StayGuests) => void;
	setDiscount: (discount: IDirectDiscount | null | undefined) => void;
	currency: string;
	/** True when a fixed discount is worth more than the stay, so it cannot be applied. */
	discountExceedsTotal: boolean;
	/** How many pets a guest can bring and pay for; 0 hides the choice. */
	maxPets: number;
}

// as the booking processor reads the pets rule's charge_type
const PER_PET = ['per unit', 'per unit per night'];
const PER_NIGHT = ['per unit per night', 'per night'];

/** The pet fee for the stay, in the property's currency. */
export const petFee = (rule: IRestriction, pets: number, nights: number) =>
	rule.charge *
	(PER_PET.includes(rule.charge_type) ? pets : 1) *
	(PER_NIGHT.includes(rule.charge_type) ? nights : 1);

const EMPTY_DATES: StayDates = { startOn: '', endOn: '' };

const toDate = (iso: string) => (iso ? new Date(iso) : null);

/**
 * The one place a stay gets priced. Holds the guest's dates, party and
 * discount, builds the length-of-stay table from the property's pricing and
 * availability, and derives the priced `stay` from them.
 */
export const useStayPricing = ({
	content,
	bookingFeeRate,
	initialDates = EMPTY_DATES,
	initialGuests,
	initialDiscount,
	petFees = false,
}: UseStayPricingOptions): StayPricing => {
	const { property, availability = [], pricing = [] } = content;

	// Content without pricing still needs a currency to render; GBP is the platform default.
	const currency = pricing[0]?.currency ?? 'GBP';

	const [dates, setDates] = useState<StayDates>(initialDates);
	const [guests, setGuests] = useState<StayGuests>(
		initialGuests ?? { adults: property.capacity, children: 0 },
	);
	const [discount, setDiscount] = useState(initialDiscount);

	const petRule = petFees
		? property.restrictions.find(
				(r) =>
					r.restriction_type === 'pets' && r.allowed && r.charge > 0,
			)
		: undefined;
	const maxPets = petRule ? (petRule.max_pets ?? property.capacity) : 0;

	const los = useMemo(
		() =>
			buildLOS(
				availability,
				pricing,
				property.capacity,
				property.advance_bookings || 1,
				1,
				bookingFeeRate,
			),
		[
			availability,
			pricing,
			property.capacity,
			property.advance_bookings,
			bookingFeeRate,
		],
	);

	const firstSelectableDate = useMemo(
		() =>
			Temporal.PlainDate.from(today())
				.add({ days: property.advance_bookings || 1 })
				.toString(),
		[property.advance_bookings],
	);

	const stay = useMemo<IStay>(() => {
		const pets = Math.min(guests.pets ?? 0, maxPets);
		const nights =
			dates.startOn && dates.endOn
				? Temporal.PlainDate.from(dates.startOn).until(dates.endOn).days
				: 0;
		// only the fees the booking processor charges: the cleaning fee, and the pet fee for the pets chosen
		const fees = [
			...property.restrictions.filter(
				(r) => r.restriction_type === 'cleaning fee',
			),
			...(petRule && pets
				? [
						{
							...petRule,
							restriction_type: 'pet fee',
							charge: petFee(petRule, pets, nights),
						},
					]
				: []),
		];
		const priced = calculatePricing(
			los,
			fees,
			discount,
			currency,
			toDate(dates.startOn),
			toDate(dates.endOn),
		);
		return {
			...guests,
			pets: petRule ? pets : undefined,
			infants: 0,
			startDate: dates.startOn || undefined,
			endDate: dates.endOn || undefined,
			appliedDiscount: discount,
			...priced,
		};
	}, [
		los,
		property.restrictions,
		petRule,
		maxPets,
		discount,
		currency,
		dates,
		guests,
	]);

	const discountExceedsTotal =
		discount?.discountType === 'fixed' && (stay.totalToPayToday ?? 0) < 0;

	const updateDates = useCallback((next: StayDates) => setDates(next), []);
	const updateGuests = useCallback((next: StayGuests) => setGuests(next), []);

	return {
		stay,
		los,
		firstSelectableDate,
		dates,
		guests,
		discount,
		setDates: updateDates,
		setGuests: updateGuests,
		setDiscount,
		currency,
		discountExceedsTotal,
		maxPets,
	};
};
